/**
 *	Namespaced `localStorage` access.
 *
 *	A factory rather than bare module functions, because the key prefix is
 *	per-app: two SPAs served from the same origin during development would
 *	otherwise share (and clobber) each other's keys. Each app makes one instance
 *	and re-exports it — see portal/src/pkg/storage/local.ts.
 */
const KeyDelimiter: string = '::';

// eslint-disable-next-line teslint/no-explicit-any
function __safeCall<T = any>(callback: CallableFunction): T {
	try {
		if (window instanceof Window) {
			return callback();
		}
	} catch {
		// @note Silently fail if window is undefined.
	}

	return undefined as unknown as T;
}

export interface LocalStorage {
	toKey(...segments: string[]): string;
	set(key: string, value: string): void;
	get<T = string>(key: string, fallbackValue?: T): T;
	remove(key: string): void;
	flush(): void;
}

export function createLocalStorage(keyPrefix: string): LocalStorage {
	function toKey(...segments: string[]): string {
		return [keyPrefix, ...segments].join(KeyDelimiter);
	}

	function set(key: string, value: string): void {
		return __safeCall(() => window.localStorage.setItem(toKey(key), value));
	}

	function get<T = string>(key: string, fallbackValue?: T): T {
		return __safeCall<T>(
			() => (window?.localStorage.getItem(toKey(key)) || fallbackValue) as T
		);
	}

	function remove(key: string): void {
		return __safeCall(() => window.localStorage.removeItem(toKey(key)));
	}

	/**
	 *	Clears this app's keys, and only this app's.
	 *
	 *	Keys are read off storage already prefixed, so they go to `removeItem`
	 *	directly — passing them through `remove` would prefix them a second time
	 *	and match nothing.
	 */
	function flush(): void {
		return __safeCall(() => {
			const prefix = `${keyPrefix}${KeyDelimiter}`;

			for (const key of Object.keys(window.localStorage)) {
				if (key.startsWith(prefix)) {
					window.localStorage.removeItem(key);
				}
			}
		});
	}

	return { toKey, set, get, remove, flush };
}
